import { useEffect, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Camera, ImageIcon, X, Plus, Sparkles } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useSession } from "@/hooks/use-session";
import { useToast } from "@/components/system";
import {
  FormInput,
  PrimaryButton,
  SecondaryButton,
  DialogFooter,
  localNowDatetime,
  browserTz,
} from "@/components/forms";
import { formatError } from "@/lib/format-error";

/**
 * BW9 — Photo-based meal logging.
 *
 * Three flows feed the same extraction pipeline:
 *   1. Camera capture (mobile, environment-facing)
 *   2. Library upload
 *   3. App-screenshot ("Use Lifesum screenshot")
 *
 * After extraction, the user lands in a Review screen with editable items
 * + confidence badges. "Log meal" persists a `meal` row with
 * `source = 'photo_meal' | 'screenshot_app'` and aggregated macros.
 */

const WORKER_URL =
  (import.meta.env.VITE_HAUS_WORKER_URL as string | undefined) ??
  "https://haus-worker.rackljo.workers.dev";

export type ExtractedItem = {
  name: string;
  quantity: number;
  unit: string;
  kcal: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  fiber_g?: number;
  confidence?: "high" | "medium" | "low";
  brand?: string | null;
  // Lookup chain provenance — set by the worker.
  //   off | usda → DB-authoritative macros (food_photo path)
  //   ai → LLM-only estimate (food_photo fall-back)
  //   screenshot → BW20.1: macros came directly from OCR of an
  //     app_item_list screenshot; no lookup ran. Screenshot is source of truth.
  //   unknown → user must review
  source?: "off" | "usda" | "ai" | "unknown" | "screenshot";
  // BW20.1 PR-B: when the result came from a fallback layer this records
  // why earlier layers were rejected. Renders below the source pill so
  // the user sees "OFF (USDA blank)" / "AI (USDA blank, OFF no match)".
  fallback_reason?: string | null;
  match_confidence?: "high" | "medium" | "low";
};

type ExtractionGroup = {
  meal_type: string | null;
  logged_at_hint: string | null;
  displayed_total_kcal: number | null;
  displayed_total_macros: { p: number; c: number; f: number } | null;
  items: ExtractedItem[];
  sum_kcal: number;
};

type ExtractionResponse = {
  items: ExtractedItem[];
  meal_groups?: ExtractionGroup[] | null;
  // BW20.1 Bug A: 3-class authoritative classifier.
  image_type?: "food_photo" | "app_summary" | "app_item_list" | null;
  entry_type?: "itemized_estimated" | "imported_summary" | "itemized_imported" | null;
  mode?: "summary" | "items" | "photo" | null;
  meal_type: string | null;
  source_type: string;
  tracker_app?: string | null;
  notes: string | null;
  cost_usd: number;
  elapsed_ms: number;
  logged_at_hint?: string | null;
  displayed_total_kcal?: number | null;
  displayed_total_macros?: { p: number; c: number; f: number } | null;
  sum_kcal?: number;
  warning?: string | null;
};

export function MealPhotoTab({
  onLogged,
  initialFiles,
}: {
  onLogged: () => void;
  initialFiles?: File[];
}) {
  const toast = useToast();
  const { session } = useSession();
  const userId = session?.user?.id;
  const qc = useQueryClient();

  const [imageFiles, setImageFiles] = useState<File[]>(initialFiles ?? []);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [extraction, setExtraction] = useState<ExtractionResponse | null>(null);
  const [items, setItems] = useState<ExtractedItem[]>([]);
  const [mealName, setMealName] = useState("");
  const [ts, setTs] = useState(localNowDatetime());
  const fileRef = useRef<HTMLInputElement | null>(null);
  const captureRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    const urls = imageFiles.map((f) => URL.createObjectURL(f));
    setImagePreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [imageFiles]);

  function reset() {
    setImageFiles([]);
    setImagePreviews([]);
    setExtraction(null);
    setItems([]);
    setMealName("");
    setTs(localNowDatetime());
  }

  const extract = useMutation({
    mutationFn: async () => {
      if (imageFiles.length === 0) throw new Error("Pick an image first");
      const { data: { session: s } } = await supabase.auth.getSession();
      if (!s?.access_token) throw new Error("Not signed in");
      const form = new FormData();
      for (const f of imageFiles) form.append("image", f);
      const resp = await fetch(`${WORKER_URL}/api/meals/extract-from-photo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${s.access_token}` },
        body: form,
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(`Extraction failed (${resp.status}): ${txt.slice(0, 200)}`);
      }
      return (await resp.json()) as ExtractionResponse;
    },
    onSuccess: (data) => {
      // Multi-meal screenshots return meal_groups instead of a flat items[].
      // For now we flatten — the user gets one review list of all items
      // and can split into multiple meals manually if they want. The
      // banner (rendered below) explains.
      const flatItems = data.meal_groups?.length
        ? data.meal_groups.flatMap((g) => g.items)
        : data.items;
      setExtraction(data);
      setItems(flatItems);
      const inferredName =
        flatItems[0]?.name ?? (data.meal_type ? capitalize(data.meal_type) : "Meal");
      if (data.meal_groups?.length) {
        setMealName(
          data.meal_groups.map((g) => capitalize(g.meal_type ?? "Meal")).join(" + "),
        );
      } else if (flatItems.length > 1) {
        setMealName(`${capitalize(data.meal_type ?? "Meal")} (${flatItems.length} items)`);
      } else {
        setMealName(inferredName);
      }
      // BW12.5 hotfix Issue 4: never trust epoch-ish dates from the model.
      // If the hint parses to a date earlier than 2020, treat it as missing
      // and fall back to either an inferred meal-type time or "now".
      const inferredTs = inferTimestamp(data.logged_at_hint, data.meal_type);
      if (inferredTs) setTs(inferredTs);
      toast.show(
        `Extracted ${flatItems.length} item${flatItems.length === 1 ? "" : "s"} · $${data.cost_usd.toFixed(3)}`,
      );
    },
    onError: (e) => toast.show(formatError(e)),
  });

  const logMeal = useMutation({
    mutationFn: async () => {
      if (!userId || !extraction) throw new Error("Not signed in");
      // BW20.1 Bug A: app_summary writes the displayed totals directly with
      // no items list (screenshot is the only authority). The other two
      // paths sum from items as before.
      const isImportedSummary =
        extraction.image_type === "app_summary" ||
        extraction.entry_type === "imported_summary";
      const totals = isImportedSummary
        ? {
            kcal: extraction.displayed_total_kcal ?? 0,
            protein: extraction.displayed_total_macros?.p ?? 0,
            carbs: extraction.displayed_total_macros?.c ?? 0,
            fat: extraction.displayed_total_macros?.f ?? 0,
            fiber: 0,
          }
        : aggregate(items);
      const foods = isImportedSummary
        ? []
        : items.map((it) => ({
            name: it.name,
            qty: it.quantity,
            unit: it.unit,
            kcal: it.kcal,
            macros: { p: it.protein_g, c: it.carbs_g, f: it.fat_g },
            confidence: it.confidence,
            source: it.source,
          }));
      const { data, error } = await supabase
        .from("meal")
        .insert({
          user_id: userId,
          ts: new Date(ts).toISOString(),
          tz: browserTz(),
          name: mealName.trim() || "Meal",
          foods,
          kcal: totals.kcal,
          macros: { p: totals.protein, c: totals.carbs, f: totals.fat },
          micros: { fiber_g: totals.fiber },
          source:
            extraction.source_type === "screenshot_app"
              ? "screenshot_app"
              : "photo_meal",
          entry_type: extraction.entry_type ?? null,
          extraction_confidence: isImportedSummary ? "high" : lowestConfidence(items),
          notes: extraction.notes ?? "",
          locked: false,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["today_meals", userId] });
      qc.invalidateQueries({ queryKey: ["nutrition_roll", userId] });
      toast.show("Meal logged from photo.");
      reset();
      onLogged();
    },
    onError: (e) => toast.show(`Couldn't log meal: ${formatError(e)}`),
  });

  // ---------------- Picker (no image yet) ----------------
  if (imageFiles.length === 0) {
    return (
      <PhotoDropzone onFiles={(files) => setImageFiles(Array.from(files).slice(0, 5))}>
        <PhotoTrustBanner />
        <p style={{ margin: 0, color: "var(--fg-2)", fontSize: "var(--type-body-s)", lineHeight: 1.5 }}>
          Snap a photo of your meal, drag images in, or upload up to 5 Lifesum /
          Cronometer / MyFitnessPal screenshots. Claude reads them, applies an
          OFF/USDA macro lookup, and pre-fills the meal.
        </p>
        <input
          ref={captureRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          onChange={(e) => setImageFiles(arrayFromFiles(e.target.files))}
          style={{ display: "none" }}
        />
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => setImageFiles(arrayFromFiles(e.target.files))}
          style={{ display: "none" }}
        />
        <PickButton
          Icon={Camera}
          label="Take photo"
          hint="Uses your camera, mobile only"
          onClick={() => captureRef.current?.click()}
        />
        <PickButton
          Icon={ImageIcon}
          label="Upload from library"
          hint="Up to 5 images — multi-page Lifesum or photos of the meal"
          onClick={() => fileRef.current?.click()}
        />
      </PhotoDropzone>
    );
  }

  // ---------------- Preview + analyze ----------------
  if (!extraction) {
    return (
      <div style={{ display: "grid", gap: "var(--space-3)" }}>
        <ThumbnailGrid
          srcs={imagePreviews}
          onClearAll={reset}
          onRemove={(i) => {
            setImageFiles((xs) => xs.filter((_, idx) => idx !== i));
          }}
        />
        <PrimaryButton
          onClick={() => extract.mutate()}
          disabled={extract.isPending}
        >
          <Sparkles size={14} strokeWidth={1.6} style={{ marginRight: 6 }} />
          {extract.isPending ? "Analyzing…" : `Analyze ${imageFiles.length === 1 ? "image" : `${imageFiles.length} images`}`}
        </PrimaryButton>
        <p style={{ margin: 0, color: "var(--fg-3)", fontSize: "var(--type-meta)" }}>
          Each call costs ~$0.01–0.03 of Anthropic API credit.
        </p>
      </div>
    );
  }

  // ---------------- Review + edit ----------------
  const isImportedSummary =
    extraction.image_type === "app_summary" ||
    extraction.entry_type === "imported_summary";
  const isItemizedImported =
    extraction.image_type === "app_item_list" ||
    extraction.entry_type === "itemized_imported";
  const totals = isImportedSummary
    ? {
        kcal: extraction.displayed_total_kcal ?? 0,
        protein: extraction.displayed_total_macros?.p ?? 0,
        carbs: extraction.displayed_total_macros?.c ?? 0,
        fat: extraction.displayed_total_macros?.f ?? 0,
        fiber: 0,
      }
    : aggregate(items);
  const lowConfidenceCount = items.filter((it) => it.confidence === "low").length;
  return (
    <div style={{ display: "grid", gap: "var(--space-3)" }}>
      <ThumbnailGrid srcs={imagePreviews} compact onClearAll={reset} />
      {isImportedSummary && (
        <ImportedSummaryChip trackerApp={extraction.tracker_app ?? null} />
      )}
      {isItemizedImported && (
        <ItemizedImportedChip trackerApp={extraction.tracker_app ?? null} />
      )}
      {!isImportedSummary && lowConfidenceCount > 0 && (
        <LowConfidenceBanner count={lowConfidenceCount} />
      )}
      {extraction.meal_groups && extraction.meal_groups.length > 1 && (
        <div
          role="status"
          style={{
            padding: "var(--space-3)",
            background: "var(--bg-3)",
            border: "1px solid var(--hair-2)",
            borderRadius: "var(--radius-card)",
            color: "var(--fg-2)",
            fontSize: "var(--type-body-s)",
            lineHeight: 1.5,
          }}
        >
          Detected{" "}
          <strong style={{ color: "var(--fg)" }}>
            {extraction.meal_groups.length} meals
          </strong>{" "}
          across these images ({extraction.meal_groups.map((g) => g.meal_type).join(", ")}).
          Items are merged below — saving logs them as one combined meal. To track them
          separately, re-upload one screenshot per meal.
        </div>
      )}
      {extraction.mode === "summary" && extraction.displayed_total_kcal != null && (
        <div
          style={{
            padding: "var(--space-4)",
            background: "var(--accent-wash)",
            border: "1px solid var(--accent-dim)",
            borderRadius: "var(--radius-card)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: "var(--space-3)",
          }}
        >
          <div>
            <div
              style={{
                fontSize: "var(--type-micro)",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                color: "var(--fg-3)",
                marginBottom: 2,
              }}
            >
              Screenshot total
            </div>
            <div className="mono" style={{ fontSize: "var(--type-body)", color: "var(--fg)" }}>
              {Math.round(extraction.displayed_total_kcal)} kcal
              {extraction.displayed_total_macros && (
                <>
                  {" · "}
                  {Math.round(extraction.displayed_total_macros.p)}P /{" "}
                  {Math.round(extraction.displayed_total_macros.c)}C /{" "}
                  {Math.round(extraction.displayed_total_macros.f)}F
                </>
              )}
            </div>
          </div>
          <span
            style={{
              fontSize: "var(--type-meta)",
              color: "var(--accent)",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            Authoritative
          </span>
        </div>
      )}
      {extraction.warning && (
        <div
          role="alert"
          style={{
            padding: "var(--space-3)",
            background: "color-mix(in oklab, var(--amber) 14%, transparent)",
            border: "1px solid var(--amber)",
            borderRadius: "var(--radius-card)",
            color: "var(--fg)",
            fontSize: "var(--type-body-s)",
          }}
        >
          {extraction.warning}
        </div>
      )}
      <FormInput label="Meal name" value={mealName} onChange={setMealName} />
      <FormInput label="When" type="datetime-local" value={ts} onChange={setTs} />

      {!isImportedSummary && (
        <>
          <header
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              color: "var(--fg-3)",
              fontSize: "var(--type-meta)",
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              marginTop: "var(--space-2)",
            }}
          >
            <span>{items.length} item{items.length === 1 ? "" : "s"}</span>
            <button
              type="button"
              onClick={() =>
                setItems((xs) => [
                  ...xs,
                  { name: "", quantity: 1, unit: "g", kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0, confidence: "low" },
                ])
              }
              style={{
                background: "transparent",
                color: "var(--accent)",
                border: "none",
                padding: 0,
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: "var(--type-meta)",
                cursor: "pointer",
              }}
            >
              <Plus size={12} strokeWidth={1.8} /> Add item
            </button>
          </header>

          <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: "var(--space-2)" }}>
            {items.map((it, i) => (
              <ItemRow
                key={i}
                item={it}
                onChange={(next) =>
                  setItems((xs) => xs.map((x, idx) => (idx === i ? next : x)))
                }
                onRemove={() => setItems((xs) => xs.filter((_, idx) => idx !== i))}
              />
            ))}
          </ul>
        </>
      )}

      <div
        style={{
          padding: "var(--space-3)",
          background: "var(--bg-3)",
          borderRadius: "var(--radius-card)",
          fontSize: "var(--type-body-s)",
          color: "var(--fg-2)",
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        <span>Total</span>
        <span className="mono" style={{ color: "var(--fg)" }}>
          {Math.round(totals.kcal)} kcal · {Math.round(totals.protein)}P / {Math.round(totals.carbs)}C / {Math.round(totals.fat)}F
        </span>
      </div>

      <DialogFooter>
        <SecondaryButton onClick={reset} disabled={logMeal.isPending}>
          Discard
        </SecondaryButton>
        <PrimaryButton
          onClick={() => logMeal.mutate()}
          disabled={
            logMeal.isPending ||
            !mealName.trim() ||
            (!isImportedSummary && items.length === 0)
          }
        >
          {logMeal.isPending ? "Saving…" : "Log meal"}
        </PrimaryButton>
      </DialogFooter>
    </div>
  );
}

/**
 * BW20.3f — drag-drop photo upload. Wraps the picker view with HTML5
 * drag listeners so a user can drop one or more images into the modal
 * (or the route, when this is mounted there) instead of clicking
 * through the file picker. The dashed-border cue activates on dragover
 * via a counted enter/leave so child enters / leaves don't clear it.
 */
function PhotoDropzone({
  children,
  onFiles,
}: {
  children: React.ReactNode;
  onFiles: (files: FileList | File[]) => void;
}) {
  const [isOver, setIsOver] = useState(false);
  const enterCount = useRef(0);

  return (
    <div
      onDragEnter={(e) => {
        if (!hasFiles(e.dataTransfer)) return;
        e.preventDefault();
        enterCount.current += 1;
        setIsOver(true);
      }}
      onDragOver={(e) => {
        if (!hasFiles(e.dataTransfer)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "copy";
      }}
      onDragLeave={() => {
        enterCount.current = Math.max(0, enterCount.current - 1);
        if (enterCount.current === 0) setIsOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        enterCount.current = 0;
        setIsOver(false);
        const files = filterImages(e.dataTransfer.files);
        if (files.length > 0) onFiles(files);
      }}
      style={{
        display: "grid",
        gap: "var(--space-3)",
        padding: isOver ? "var(--space-4)" : 0,
        border: isOver ? "2px dashed var(--accent)" : "2px dashed transparent",
        borderRadius: "var(--radius-card)",
        background: isOver ? "var(--accent-wash)" : "transparent",
        transition: "border-color var(--dur-fast) var(--ease-out), background var(--dur-fast) var(--ease-out), padding var(--dur-fast) var(--ease-out)",
      }}
    >
      {isOver && (
        <div
          aria-live="polite"
          style={{
            fontSize: "var(--type-body-s)",
            color: "var(--accent)",
            fontWeight: 500,
          }}
        >
          Drop image to extract
        </div>
      )}
      {children}
    </div>
  );
}

function hasFiles(dt: DataTransfer | null): boolean {
  if (!dt) return false;
  return Array.from(dt.types ?? []).includes("Files");
}

export function filterImages(files: FileList | null): File[] {
  if (!files) return [];
  return Array.from(files).filter((f) => /^image\//i.test(f.type) || /\.(heic|heif|jpe?g|png|webp)$/i.test(f.name));
}

function PickButton({
  Icon,
  label,
  hint,
  onClick,
}: {
  Icon: typeof Camera;
  label: string;
  hint: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "grid",
        gridTemplateColumns: "44px 1fr",
        alignItems: "center",
        gap: "var(--space-3)",
        padding: "var(--space-3) var(--space-4)",
        background: "var(--bg-2)",
        border: "1px solid var(--hair-2)",
        borderRadius: "var(--radius-card)",
        color: "var(--fg)",
        cursor: "pointer",
        textAlign: "left",
      }}
    >
      <span
        aria-hidden
        style={{
          width: 44,
          height: 44,
          borderRadius: "var(--radius-card)",
          background: "var(--accent-wash)",
          color: "var(--accent)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon size={20} strokeWidth={1.5} />
      </span>
      <span>
        <span style={{ display: "block", fontSize: "var(--type-body)", fontWeight: 500 }}>
          {label}
        </span>
        <span style={{ display: "block", fontSize: "var(--type-meta)", color: "var(--fg-3)", marginTop: 2 }}>
          {hint}
        </span>
      </span>
    </button>
  );
}

function ThumbnailGrid({
  srcs,
  onClearAll,
  onRemove,
  compact,
}: {
  srcs: string[];
  onClearAll: () => void;
  onRemove?: (i: number) => void;
  compact?: boolean;
}) {
  if (srcs.length === 0) return null;
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: srcs.length > 1 ? "repeat(auto-fill, minmax(120px, 1fr))" : "1fr",
        gap: 6,
      }}
    >
      {srcs.map((src, i) => (
        <div key={i} style={{ position: "relative", borderRadius: "var(--radius-card)", overflow: "hidden" }}>
          <img
            src={src}
            alt={`Image ${i + 1}`}
            style={{
              width: "100%",
              maxHeight: compact ? 120 : 240,
              objectFit: "cover",
              display: "block",
            }}
          />
          <button
            type="button"
            onClick={() => (onRemove ? onRemove(i) : onClearAll())}
            aria-label="Remove image"
            style={{
              position: "absolute",
              top: 6,
              right: 6,
              background: "rgba(10,11,13,0.75)",
              color: "var(--fg)",
              border: "none",
              borderRadius: "var(--radius-chip)",
              padding: 4,
              cursor: "pointer",
              display: "inline-flex",
            }}
          >
            <X size={12} strokeWidth={1.6} />
          </button>
        </div>
      ))}
    </div>
  );
}

function ItemRow({
  item,
  onChange,
  onRemove,
}: {
  item: ExtractedItem;
  onChange: (next: ExtractedItem) => void;
  onRemove: () => void;
}) {
  const conf = item.confidence ?? "medium";
  const confColor =
    conf === "high" ? "var(--accent)" : conf === "medium" ? "var(--amber)" : "var(--brick)";
  const source = item.source ?? "ai";
  const sourceColor =
    source === "off" || source === "usda" || source === "screenshot"
      ? "var(--accent)"
      : source === "ai"
      ? "var(--amber)"
      : "var(--brick)";
  const sourceLabel =
    source === "off"
      ? "OFF"
      : source === "usda"
      ? "USDA"
      : source === "screenshot"
      ? "Screenshot"
      : source === "ai"
      ? "AI"
      : "?";
  return (
    <li
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 28px",
        gap: "var(--space-2)",
        padding: "var(--space-3)",
        background: "var(--bg-2)",
        border: "1px solid var(--hair)",
        borderRadius: "var(--radius-card)",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)", minWidth: 0 }}>
        {/* Name — primary, full width */}
        <input
          value={item.name}
          onChange={(e) => onChange({ ...item, name: e.target.value })}
          style={{
            ...cellInput(),
            fontSize: "var(--type-body)",
            fontWeight: 500,
            padding: "6px 10px",
          }}
          placeholder="Item name"
        />
        {/* Source + confidence pills */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          <SourcePill label={sourceLabel} color={sourceColor} />
          {item.fallback_reason && (
            <span
              style={{
                fontSize: "var(--type-micro)",
                color: "var(--fg-3)",
                letterSpacing: "0.04em",
              }}
            >
              ({item.fallback_reason})
            </span>
          )}
          <ConfidencePill label={conf} color={confColor} />
          {item.brand && (
            <span
              style={{
                fontSize: "var(--type-meta)",
                color: "var(--fg-3)",
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                maxWidth: 140,
              }}
            >
              {item.brand}
            </span>
          )}
        </div>
        {/* Macros — secondary row, smaller */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "60px 60px 60px 60px 60px",
            gap: 6,
          }}
        >
          <CellLabeled label="qty" value={item.quantity} onChange={(v) => onChange({ ...item, quantity: v })} />
          <CellLabeled label="kcal" value={item.kcal} onChange={(v) => onChange({ ...item, kcal: v })} />
          <CellLabeled label="P" value={item.protein_g} onChange={(v) => onChange({ ...item, protein_g: v })} />
          <CellLabeled label="C" value={item.carbs_g} onChange={(v) => onChange({ ...item, carbs_g: v })} />
          <CellLabeled label="F" value={item.fat_g} onChange={(v) => onChange({ ...item, fat_g: v })} />
        </div>
      </div>
      <button
        type="button"
        onClick={onRemove}
        aria-label="Remove item"
        style={{
          background: "transparent",
          border: "none",
          color: "var(--fg-3)",
          cursor: "pointer",
          padding: 4,
          alignSelf: "start",
        }}
      >
        <X size={14} strokeWidth={1.5} />
      </button>
    </li>
  );
}

function CellLabeled({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (v: number) => void;
}) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
      <span
        style={{
          fontSize: "var(--type-micro)",
          color: "var(--fg-3)",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}
      >
        {label}
      </span>
      <input
        inputMode="decimal"
        value={value || ""}
        onChange={(e) => onChange(Number(e.target.value || 0))}
        style={cellInput()}
      />
    </label>
  );
}

function SourcePill({ label, color }: { label: string; color: string }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "2px 6px",
        background: `color-mix(in oklab, ${color} 14%, transparent)`,
        color,
        fontSize: "var(--type-micro)",
        fontWeight: 600,
        borderRadius: "var(--radius-chip)",
        letterSpacing: "0.04em",
      }}
    >
      {label}
    </span>
  );
}

function ConfidencePill({ label, color }: { label: string; color: string }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        fontSize: "var(--type-micro)",
        color,
      }}
    >
      <span
        aria-hidden
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: color,
          display: "inline-block",
        }}
      />
      {label}
    </span>
  );
}

function cellInput(): React.CSSProperties {
  return {
    background: "var(--bg-3)",
    border: "1px solid var(--hair-2)",
    borderRadius: "var(--radius-chip)",
    color: "var(--fg)",
    fontSize: "var(--type-body-s)",
    padding: "4px 8px",
    width: "100%",
    minWidth: 0,
  };
}

function aggregate(items: ExtractedItem[]) {
  return items.reduce(
    (acc, it) => ({
      kcal: acc.kcal + Number(it.kcal || 0),
      protein: acc.protein + Number(it.protein_g || 0),
      carbs: acc.carbs + Number(it.carbs_g || 0),
      fat: acc.fat + Number(it.fat_g || 0),
      fiber: acc.fiber + Number(it.fiber_g || 0),
    }),
    { kcal: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 },
  );
}

function lowestConfidence(items: ExtractedItem[]): "high" | "medium" | "low" {
  if (items.some((i) => i.confidence === "low")) return "low";
  if (items.some((i) => i.confidence === "medium")) return "medium";
  return "high";
}

function capitalize(s: string): string {
  return s.length ? s[0].toUpperCase() + s.slice(1) : s;
}

function arrayFromFiles(files: FileList | null): File[] {
  if (!files) return [];
  return Array.from(files).slice(0, 5);
}

function toLocalDatetime(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

/**
 * Infer a `datetime-local` value from the model's hint, falling back to
 * meal-type heuristics, then to "now". Reject epoch-ish dates (year < 2020) —
 * the upstream prompt occasionally returns "1970-01-01T19:00:00Z" when no time
 * is visible, and that placeholder must never reach the meal row.
 */
function inferTimestamp(
  hint: string | null | undefined,
  mealType: string | null | undefined,
): string | null {
  if (hint) {
    const d = new Date(hint);
    if (!Number.isNaN(d.getTime()) && d.getFullYear() >= 2020) {
      return toLocalDatetime(d);
    }
  }
  // Hint was missing or invalid — try meal type. We anchor to "today" in the
  // user's local timezone so a Lifesum screenshot with breakfast logged earlier
  // still maps onto the correct day.
  if (mealType) {
    const slot: Record<string, [number, number]> = {
      breakfast: [7, 30],
      lunch: [12, 30],
      dinner: [19, 0],
      snack: [15, 30],
    };
    const t = slot[mealType.toLowerCase()];
    if (t) {
      const now = new Date();
      now.setHours(t[0], t[1], 0, 0);
      return toLocalDatetime(now);
    }
  }
  return null;
}


/**
 * BW18.5 PR-5.D: photo logging is secondary, not primary. The banner
 * sits above the picker so users see the trust caveat before they even
 * snap. Research §6 confirms portion estimation is the failure mode that
 * silently creates a false calorie deficit.
 */
function PhotoTrustBanner() {
  return (
    <div
      style={{
        padding: 'var(--space-3)',
        background: 'var(--accent-wash)',
        border: '1px solid var(--accent-dim)',
        borderRadius: 'var(--radius-card)',
        fontSize: 'var(--type-body-s)',
        color: 'var(--fg)',
        lineHeight: 1.5,
      }}
    >
      Portion estimates from photos are uncertain. Confirm each item before saving so the day's totals stay honest.
    </div>
  );
}

/**
 * BW20.1 Bug A — chip shown when image_type='app_summary'. The screenshot
 * carried only daily/meal totals, so Haus stores the totals as the meal
 * with no item list. NO warning, NO low-confidence banner. The chip is
 * adherence-neutral: it tells the user where the data came from, nothing
 * more.
 */
function ImportedSummaryChip({ trackerApp }: { trackerApp: string | null }) {
  const label = trackerApp ? prettyTrackerApp(trackerApp) : "tracker app";
  return (
    <div
      style={{
        padding: "var(--space-3)",
        background: "var(--bg-2)",
        border: "1px solid var(--hair-2)",
        borderRadius: "var(--radius-card)",
        fontSize: "var(--type-body-s)",
        color: "var(--fg-2)",
        lineHeight: 1.5,
      }}
    >
      Imported from {label}, totals only.
    </div>
  );
}

/**
 * BW20.1 Bug A — chip shown when image_type='app_item_list'. Per-item
 * macros came directly from OCR of the screenshot; the lookup chain was
 * deliberately skipped. The screenshot is the source of truth.
 */
function ItemizedImportedChip({ trackerApp }: { trackerApp: string | null }) {
  const label = trackerApp ? prettyTrackerApp(trackerApp) : "tracker app";
  return (
    <div
      style={{
        padding: "var(--space-3)",
        background: "var(--bg-2)",
        border: "1px solid var(--hair-2)",
        borderRadius: "var(--radius-card)",
        fontSize: "var(--type-body-s)",
        color: "var(--fg-2)",
        lineHeight: 1.5,
      }}
    >
      {label} item list. Macros come from the screenshot, not a lookup.
    </div>
  );
}

function prettyTrackerApp(slug: string): string {
  switch (slug) {
    case "lifesum_de":
    case "lifesum_en":
      return "Lifesum";
    case "cronometer":
      return "Cronometer";
    case "myfitnesspal":
      return "MyFitnessPal";
    default:
      return slug;
  }
}

/**
 * Low-confidence banner shown after extraction when one or more items
 * carry confidence === 'low'. The user must scan and adjust before save.
 */
function LowConfidenceBanner({ count }: { count: number }) {
  return (
    <div
      role='alert'
      style={{
        padding: 'var(--space-3)',
        background: 'var(--severity-warn-wash, var(--accent-wash))',
        border: '1px solid var(--severity-warn, var(--accent-dim))',
        borderRadius: 'var(--radius-card)',
        fontSize: 'var(--type-body-s)',
        color: 'var(--fg)',
        lineHeight: 1.5,
      }}
    >
      <strong>{count}</strong> low-confidence item{count === 1 ? '' : 's'}. Adjust portions before saving so today's nutrition reflects what you actually ate.
    </div>
  );
}

